import { useEffect, useState, useCallback, Fragment } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Spinner,
  Collapse,
  Card,
  CardBody,
  Table,
  Badge,
} from 'reactstrap';
import Select from 'react-select';
import { useDropzone } from 'react-dropzone';
import FeatherIcon from 'feather-icons-react';
import jwt_decode from 'jwt-decode';
import '../../moveinout.scss';
import {
  requestAttachmentUpload,
  confirmAttachmentUpload,
  getMultipleSignedUrls,
  updateMoveInOutInspection,
  createMoveInOutInspection,
} from './FormikHelpers/Api';
import { SignatureCreator } from '../../../NewMasterSheet/LeaseProcess/Flow/StepperComponents/lease/components/steps/components';
import {
  conditionLabels,
  defaultItems,
  isSectionComplete,
  sectionDisplayNames,
  sectionIcons,
  setNestedTouched,
} from './FormikHelpers/utils';
import { moveInOutInspectionSchema, validateInspectionStep } from './FormikHelpers';
import { setLoading } from '../../../../../store/actions';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';

const MediaViewerModal = ({ isOpen, toggle, mediaUrl, mediaType, title }) => (
  <Modal isOpen={isOpen} toggle={toggle} centered size="xl" backdrop="static">
    <ModalHeader toggle={toggle} className="border-0 pb-2 bg-dark text-white">
      {title || 'Media Preview'}
    </ModalHeader>
    <ModalBody className="text-center py-5 bg-black">
      {mediaType?.startsWith('video/') ? (
        <video
          controls
          style={{
            maxHeight: '85vh',
            maxWidth: '100%',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
          }}
        >
          <source src={mediaUrl} type={mediaType} />
          Your browser does not support the video tag.
        </video>
      ) : (
        <img
          src={mediaUrl}
          alt={title}
          style={{
            maxHeight: '85vh',
            maxWidth: '100%',
            objectFit: 'contain',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
          }}
        />
      )}
    </ModalBody>
    <ModalFooter className="border-0 bg-dark justify-content-center">
      <Button color="secondary" onClick={toggle}>
        Close
      </Button>
    </ModalFooter>
  </Modal>
);

const EditableDate = ({ fieldPath, formik, disabled = false }) => {
  const [editing, setEditing] = useState(false);
  const getValue = () => {
    const parts = fieldPath.split('.');
    let val = formik.values;
    for (const part of parts) val = val?.[part];
    return val || '';
  };
  const rawValue = getValue();
  const value = rawValue ? rawValue.split('T')[0] : '';
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '____________________';
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return '____________________';
    const date = new Date(year, month - 1, day);
    const monthName = date.toLocaleString('default', { month: 'long' });
    const suffix =
      [1, 21, 31].includes(day)
        ? 'st'
        : [2, 22].includes(day)
          ? 'nd'
          : [3, 23].includes(day)
            ? 'rd'
            : 'th';
    return `${monthName} ${day}${suffix}, ${year}`;
  };
  const displayed = formatDateDisplay(value);
  if (disabled) {
    return <span style={{ fontWeight: '600' }}>{displayed}</span>;
  }
  if (editing) {
    return (
      <input
        type="date"
        value={value}
        onChange={(e) => formik.setFieldValue(fieldPath, e.target.value || null)}
        onBlur={() => setEditing(false)}
        autoFocus
        style={{
          borderRadius: '4px',
          padding: '4px 8px',
          fontSize: '1rem',
        }}
      />
    );
  }
  return (
    <span
      onClick={() => setEditing(true)}
      style={{
        cursor: 'pointer',
        fontWeight: '600',
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
      }}
    >
      {displayed}
      <FeatherIcon icon="edit-2" size={14} className="ms-1 text-danger align-text-bottom" />
    </span>
  );
};

const EditableText = ({
  fieldPath,
  formik,
  placeholder = '____________________',
  maxLength,
  inputWidth = '200px',
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const getValue = () => {
    const parts = fieldPath.split('.');
    let val = formik.values;
    for (const part of parts) val = val?.[part];
    return val || '';
  };
  const value = getValue();
  if (disabled) {
    return <span style={{ fontWeight: '600' }}>{value || placeholder}</span>;
  }
  if (editing) {
    return (
      <input
        type="text"
        value={value}
        maxLength={maxLength}
        onChange={(e) => formik.setFieldValue(fieldPath, e.target.value || '')}
        onBlur={() => setEditing(false)}
        autoFocus
        placeholder={placeholder}
        style={{
          borderRadius: '4px',
          padding: '4px 8px',
          fontSize: '1rem',
          minWidth: inputWidth,
          width: inputWidth,
        }}
      />
    );
  }
  return (
    <span
      onClick={() => setEditing(true)}
      style={{
        cursor: 'pointer',
        fontWeight: '600',
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
        minWidth: inputWidth,
        width: inputWidth,
      }}
    >
      {value || placeholder}
      <FeatherIcon icon="edit-2" size={14} className="ms-1 text-danger align-text-bottom" />
    </span>
  );
};

const DigitalSignatureField = ({
  fieldPath,
  formik,
  placeholder = 'Click to sign',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const getValue = () => {
    const parts = fieldPath.split('.');
    let val = formik.values;
    for (const part of parts) val = val?.[part];
    return val || '';
  };
  const value = getValue();
  const handleSign = ({ signatureData }) => {
    formik.setFieldValue(fieldPath, signatureData);
    setIsOpen(false);
  };
  const handleClear = () => {
    formik.setFieldValue(fieldPath, '');
  };
  if (disabled) {
    return (
      <span style={{ minWidth: '320px', minHeight: '20px', verticalAlign: 'middle' }}>
        {value ? (
          <img
            src={value}
            alt="Signature"
            style={{
              maxHeight: '15px',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
          />
        ) : (
          <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
            {placeholder}
          </span>
        )}
      </span>
    );
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      <span
        onClick={() => setIsOpen(true)}
        style={{
          cursor: 'pointer',
          minWidth: '250px',
          minHeight: '20px',
          verticalAlign: 'middle',
        }}
      >
        {value ? (
          <img
            src={value}
            alt="Signature"
            style={{
              maxHeight: '15px',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
          />
        ) : (
          <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
            {placeholder}
          </span>
        )}
        <FeatherIcon icon="edit-2" size={16} className="ms-2 text-danger align-text-bottom" />
      </span>
      {value && (
        <Button
          color="link"
          size="sm"
          className="p-0 text-danger"
          style={{ fontSize: '0.9rem', lineHeight: 1 }}
          onClick={handleClear}
          title="Remove signature"
        >
          <FeatherIcon icon="x" size={18} />
        </Button>
      )}
      <SignatureCreator
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSign={handleSign}
        initialSignature={value}
        width={600}
        height={200}
        disableSignAndSave={true}
      />
    </div>
  );
};

const GlobalConditionModal = ({
  isOpen,
  toggle,
  sectionTitle,
  sectionKey,
  allowEdit,
  phase,
  formik,
}) => {
  const sectionItems = (formik.values.inspection_items || []).filter(
    item => item.section === sectionKey
  );
  const codes = sectionItems.map(item =>
    phase === 'move_in'
      ? item.move_in_condition?.code
      : item.move_out_condition?.code
  );
  const uniqueCodes = [...new Set(codes.filter(c => c != null))];
  const displayValue =
    uniqueCodes.length === 1
      ? uniqueCodes[0]
      : codes.some(c => c != null)
        ? 'mixed'
        : null;
  const options = [
    { value: null, label: <em>Not Set (All)</em> },
    ...Object.entries(conditionLabels).map(([code, label]) => ({
      value: Number(code),
      label,
    })),
  ];
  const selectedOption =
    displayValue === 'mixed'
      ? { value: 'mixed', label: <em>Mixed Values</em> }
      : options.find(opt => opt.value === displayValue) || null;

  if (!allowEdit) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} centered size="md">
        <ModalHeader toggle={toggle}>
          View condition for <strong>{sectionTitle}</strong>
        </ModalHeader>
        <ModalBody>
          <div className="p-3 bg-light border rounded fw-medium">
            {displayValue === 'mixed'
              ? 'Mixed'
              : displayValue != null
                ? conditionLabels[displayValue]
                : 'Not Set'}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="d-flex align-items-center gap-2">
        <span className={`${phase === 'move_in' ? 'text-primary' : 'text-danger'}`}>
          Set condition for {sectionTitle} {phase === 'move_in' ? 'Move-In' : 'Move-Out'}
        </span>
      </ModalHeader>
      <ModalBody>
        <Select
          value={selectedOption}
          options={options}
          isSearchable
          placeholder={<em>Not Set (All)</em>}
          className="mb-2"
          styles={{
            control: base => ({
              ...base,
              minHeight: 44,
              borderRadius: 6,
              borderColor: '#ced4da',
              boxShadow: 'none',
            }),
            menu: base => ({
              ...base,
              zIndex: 9999,
            }),
          }}
          onChange={(option) => {
            const newCode = option ? option.value : null;
            formik.values.inspection_items.forEach((item, idx) => {
              if (item.section === sectionKey) {
                const path =
                  phase === 'move_in'
                    ? `inspection_items.${idx}.move_in_condition.code`
                    : `inspection_items.${idx}.move_out_condition.code`;
                formik.setFieldValue(path, newCode);
              }
            });
          }}
        />
        <div className="text-muted small mt-2">
          This will apply the selected{' '}
          <i>{phase === 'move_in' ? 'move-in' : 'move-out'}</i> condition
          to <i>all items</i> in this section.
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-end">
        <Button color="secondary" onClick={toggle}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const InspectionItemRow = ({
  index,
  name,
  move_in_condition,
  move_out_condition,
  formik,
  onDrop,
  removePendingMedia,
  setSelectedMedia,
  pendingMedia,
  signedUrlCache,
  getMediaDisplayUrl,
  isEdit,
  inspectionId,
  allowMoveInEdit,
  allowMoveOutEdit,
}) => {
  const ALLOWED_FILE_TYPES = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/mov,video/webm,video/x-msvideo,video/x-matroska';
  const inDropzone = useDropzone({
    onDrop: (files) => onDrop(files, index, 'move_in'),
    accept: ALLOWED_FILE_TYPES,
    multiple: true,
    disabled: !allowMoveInEdit,
  });
  const outDropzone = useDropzone({
    onDrop: (files) => onDrop(files, index, 'move_out'),
    accept: ALLOWED_FILE_TYPES,
    multiple: true,
    disabled: !allowMoveOutEdit,
  });
  const pendingIn = pendingMedia[`${index}-move_in`] || [];
  const pendingOut = pendingMedia[`${index}-move_out`] || [];
  const attachments_in = move_in_condition?.attachments || [];
  const attachments_out = move_out_condition?.attachments || [];
  const shouldShowRequiredHint = (condition, pendingCount, savedCount) =>
    [2, 3, 4].includes(condition?.code) && pendingCount + savedCount === 0;

  const renderConditionDropdown = (value, onChange, disabled) => {
    const options = [
      { value: null, label: <em>Not Set</em> },
      ...Object.entries(conditionLabels).map(([code, label]) => ({
        value: Number(code),
        label,
      })),
    ];
    const selected = options.find((opt) => opt.value === value) || null;
    if (disabled) {
      return (
        <div className="text-center py-1 fw-medium">
          {value != null && conditionLabels[value] ? conditionLabels[value] : <em className="text-muted">Not Set</em>}
        </div>
      );
    }
    return (
      <Select
        value={selected}
        onChange={(option) => onChange(option ? option.value : null)}
        options={options}
        isDisabled={disabled}
        placeholder={<em>Not Set</em>}
        isSearchable
        menuPosition="fixed"
        className="react-select-container"
        classNamePrefix="react-select"
        menuPlacement="auto"
      />
    );
  };

  const handleMediaClick = async (attachment, phaseTitle) => {
    const url = await getMediaDisplayUrl(attachment);
    if (url && !url.includes('placeholder')) {
      const isVideo = /\.(mp4|mov|avi|wmv|webm)$/i.test(attachment);
      setSelectedMedia({
        url,
        title: `${name} - ${phaseTitle}`,
        type: isVideo ? 'video/mp4' : 'image/jpeg',
      });
    }
  };

  const renderMediaThumbnail = (file, cacheKey, phase, idx, isPending = false) => {
    const isVideo =
      file.type?.startsWith('video/') ||
      (typeof file === 'string' && /\.(mp4|mov|avi|wmv|webm)$/i.test(file));
    const src = isPending ? file.preview : (signedUrlCache[cacheKey] || 'https://via.placeholder.com/60?text=Loading...');
    const borderColor = phase === 'move_in' ? '#3b82f6' : '#ef4444';
    return (
      <div key={isPending ? `pending-${phase}-${idx}` : `${phase}-${idx}`} className="position-relative">
        {isVideo ? (
          <div
            className="rounded shadow-sm d-flex align-items-center justify-content-center"
            style={{ width: 60, height: 60, backgroundColor: '#000', cursor: 'pointer', border: `2px solid ${isPending ? borderColor : '#10b981'}` }}
            onClick={() => {
              const title = `${name} - ${phase === 'move_in' ? 'Move-In' : 'Move-Out'}${isPending ? ' (Pending)' : ''}`;
              setSelectedMedia({ url: isPending ? file.preview : null, title, type: file.type || 'video/mp4' });
            }}
          >
            <FeatherIcon icon="play-circle" size={32} className="text-white" />
          </div>
        ) : (
          <img
            src={src}
            alt={isPending ? 'pending' : phase}
            className="rounded shadow-sm"
            style={{ width: 60, height: 60, objectFit: 'cover', cursor: 'pointer', border: `2px solid ${isPending ? borderColor : '#10b981'}` }}
            onClick={() => {
              const title = `${name} - ${phase === 'move_in' ? 'Move-In' : 'Move-Out'}${isPending ? ' (Pending)' : ''}`;
              setSelectedMedia({ url: isPending ? file.preview : null, title, type: 'image/jpeg' });
            }}
          />
        )}
        {isPending && (
          <Button
            color="danger"
            size="sm"
            className="position-absolute rounded-circle p-0"
            style={{ width: 18, height: 18, top: -6, right: -6, fontSize: '0.7rem' }}
            onClick={() => removePendingMedia(index, phase, idx)}
          >
            ×
          </Button>
        )}
      </div>
    );
  };

  return (
    <tr className="align-middle">
      <td className="fw-semibold px-4 py-3" style={{ fontSize: '0.9rem' }}>
        <div className="d-flex align-items-center">
          <FeatherIcon icon="check-square" size={16} className="me-2 text-muted" />
          {name}
        </div>
      </td>
      <td className="text-center px-3 py-3">
        {renderConditionDropdown(
          move_in_condition?.code,
          (val) => formik.setFieldValue(`inspection_items.${index}.move_in_condition.code`, val),
          !allowMoveInEdit
        )}
      </td>
      <td className="px-3 py-3">
        {allowMoveInEdit ? (
          <Input
            type="text"
            value={move_in_condition?.comments || ''}
            onChange={(e) => formik.setFieldValue(`inspection_items.${index}.move_in_condition.comments`, e.target.value)}
            placeholder="Add comments..."
          />
        ) : (
          <p className="mb-0 text-muted">{move_in_condition?.comments || 'No comments'}</p>
        )}
      </td>
      <td className="text-center px-2 py-3 align-middle">
        {allowMoveInEdit ? (
          <div
            {...inDropzone.getRootProps()}
            className="border rounded p-2 mb-2 d-inline-flex flex-column align-items-center gap-1"
            style={{ cursor: 'pointer', backgroundColor: '#f0f9ff', border: '2px dashed #3b82f6', width: '120px' }}
          >
            <input {...inDropzone.getInputProps()} />
            <div className="d-flex align-items-center gap-2">
              <FeatherIcon icon="camera" size={16} className="text-primary" />
              <div className="text-center">
                <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>
                  Click or drop ({attachments_in.length + pendingIn.length})
                </small>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="border rounded p-2 mb-2 d-inline-flex flex-column align-items-center gap-1"
            style={{ cursor: 'default', backgroundColor: '#f0f9ff', border: '2px solid #ccc', width: '120px' }}
          >
            <div className="d-flex align-items-center gap-2">
              <FeatherIcon icon="camera" size={16} className="text-primary" />
              <div className="text-center">
                <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>
                  Media ({attachments_in.length})
                </small>
              </div>
            </div>
          </div>
        )}
        {shouldShowRequiredHint(move_in_condition, pendingIn.length, attachments_in.length) && allowMoveInEdit && (
          <small className="text-danger d-block mt-1">* Required: at least 1 photo/video</small>
        )}
        <div className="d-flex flex-wrap gap-2 justify-content-center mt-2">
          {pendingIn.map((p, i) => renderMediaThumbnail(p, null, 'move_in', i, true))}
          {attachments_in.map((att, i) => renderMediaThumbnail(att, att, 'move_in', i, false))}
        </div>
        {!isEdit && pendingIn.length > 0 && (
          <small className="text-muted d-block mt-2 text-center">Will upload after save</small>
        )}
      </td>
      <td className="text-center px-3 py-3">
        {renderConditionDropdown(
          move_out_condition?.code,
          (val) => formik.setFieldValue(`inspection_items.${index}.move_out_condition.code`, val),
          !allowMoveOutEdit
        )}
      </td>
      <td className="px-3 py-3">
        {allowMoveOutEdit ? (
          <Input
            type="text"
            value={move_out_condition?.comments || ''}
            onChange={(e) => formik.setFieldValue(`inspection_items.${index}.move_out_condition.comments`, e.target.value)}
            placeholder="Add comments..."
          />
        ) : (
          <p className="mb-0 text-muted">{move_out_condition?.comments || 'No comments'}</p>
        )}
      </td>
      <td className="text-center px-2 py-3 align-middle">
        {allowMoveOutEdit ? (
          <div
            {...outDropzone.getRootProps()}
            className="border rounded p-2 mb-2 d-inline-flex flex-column align-items-center gap-1"
            style={{ cursor: 'pointer', backgroundColor: '#fef2f2', border: '2px dashed #ef4444', width: '120px' }}
          >
            <input {...outDropzone.getInputProps()} />
            <div className="d-flex align-items-center gap-2">
              <FeatherIcon icon="camera" size={16} className="text-danger" />
              <div className="text-center">
                <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>
                  Click or drop ({attachments_out.length + pendingOut.length})
                </small>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="border rounded p-2 mb-2 d-inline-flex flex-column align-items-center gap-1"
            style={{ cursor: 'default', backgroundColor: '#fef2f2', border: '2px solid #ccc', width: '120px' }}
          >
            <div className="d-flex align-items-center gap-2">
              <FeatherIcon icon="camera" size={16} className="text-danger" />
              <div className="text-center">
                <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>
                  Media ({attachments_out.length})
                </small>
              </div>
            </div>
          </div>
        )}
        {shouldShowRequiredHint(move_out_condition, pendingOut.length, attachments_out.length) && allowMoveOutEdit && (
          <small className="text-danger d-block mt-1">* Required: at least 1 photo/video</small>
        )}
        <div className="d-flex flex-wrap gap-2 justify-content-center mt-2">
          {pendingOut.map((p, i) => renderMediaThumbnail(p, null, 'move_out', i, true))}
          {attachments_out.map((att, i) => renderMediaThumbnail(att, `out-${index}-${i}`, 'move_out', i, false))}
        </div>
        {!isEdit && pendingOut.length > 0 && (
          <small className="text-muted d-block mt-2 text-center">Will upload after save</small>
        )}
      </td>
    </tr>
  );
};

const GenerateMoveInOutInspectionModal = ({
  isModalOpen,
  setIsModalOpen,
  formik,
  isEdit = false,
  currentPhase,
  previewMode = false,
  onSaveSuccess,
}) => {
  const dispatch = useDispatch();

  const [completedSections, setCompletedSections] = useState(new Set());
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  useEffect(() => {
    const newCompleted = new Set();
    Object.keys(sectionDisplayNames).forEach(sectionKey => {
      if (isSectionComplete(sectionKey, formik.values, formik.touched, formik.errors, currentPhase)) {
        newCompleted.add(sectionKey);
      }
    });

    const prev = [...completedSections].sort();
    const next = [...newCompleted].sort();
    if (JSON.stringify(prev) !== JSON.stringify(next)) {
      setCollapsedSections(prevCollapsed => {
        const updated = new Set(prevCollapsed);
        newCompleted.forEach(key => {
          if (!completedSections.has(key)) {
            updated.add(key);
          }
        });
        completedSections.forEach(key => {
          if (!newCompleted.has(key)) {
            updated.delete(key);
          }
        });
        return updated;
      });
      setCompletedSections(newCompleted);
    }
  }, [formik.values, formik.touched, formik.errors]);

  const toggleSection = (key) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isSectionOpen = (key) => !collapsedSections.has(key);

  const [pendingMedia, setPendingMedia] = useState({});
  const [selectedMedia, setSelectedMedia] = useState({ url: '', title: '', type: '' });
  const [userRole, setUserRole] = useState(null);
  const [signedUrlCache, setSignedUrlCache] = useState({});
  const [globalConditionModal, setGlobalConditionModal] = useState({
    isOpen: false,
    sectionKey: null,
    phase: null,
  });

  const is2aFilled = (phase) => !!formik.values?.[phase]?.['2']?.a?.tenant_signature?.trim();
  const is2bFilled = (phase) => {
    const data = formik.values?.[phase]?.['2']?.b || {};
    return (data.tenant_name?.trim?.() || '').length > 0 || (data.disagreement_reasons?.trim?.() || '').length > 0;
  };
  const is2cFilled = (phase) => !!formik.values?.[phase]?.['2']?.c?.landlord_refusal_signature?.trim();
  const isOption2aDisabled = (phase) => is2bFilled(phase) || is2cFilled(phase);
  const isOption2bDisabled = (phase) => is2aFilled(phase) || is2cFilled(phase);
  const isOption2cDisabled = (phase) => is2aFilled(phase) || is2bFilled(phase);

  const openGlobalConditionModal = (sectionKey, phase) => {
    setGlobalConditionModal({ isOpen: true, sectionKey, phase });
  };

  const closeGlobalConditionModal = () => {
    setGlobalConditionModal({ isOpen: false, sectionKey: null, phase: null });
  };

  const isMoveInPhase = [1, 2].includes(currentPhase);
  const isMoveOutPhase = [3, 4].includes(currentPhase);
  const allowMoveInEdit = !previewMode && isMoveInPhase;
  const allowMoveOutEdit = !previewMode && isMoveOutPhase;

  const getMediaKey = useCallback((attachment) => {
    if (typeof attachment !== 'string') return null;
    try {
      const url = new URL(attachment);
      return url.pathname.replace(/^\/+/, '');
    } catch {
      return attachment;
    }
  }, []);

  const getMediaDisplayUrl = useCallback(async (attachment) => {
    const key = getMediaKey(attachment);
    if (!key) return 'https://via.placeholder.com/60?text=Invalid';
    if (signedUrlCache[key]) return signedUrlCache[key];
    try {
      const urlMap = await getMultipleSignedUrls([key]);
      const signed = urlMap[key];
      if (signed) {
        setSignedUrlCache((p) => ({ ...p, [key]: signed }));
        return signed;
      }
    } catch (err) {
      console.error('Signed URL fetch failed:', err);
    }
    return 'https://via.placeholder.com/60?text=Error';
  }, [signedUrlCache]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) setUserRole(jwt_decode(token).role);
  }, []);

  useEffect(() => {
    if (!isEdit && (!formik.values.inspection_items || formik.values.inspection_items.length === 0)) {
      const allItems = Object.entries(defaultItems).flatMap(([section, names]) =>
        names.map((name) => ({
          section,
          name,
          move_in_condition: { code: null, comments: '', attachments: [], recorded_at: new Date() },
          move_out_condition: { code: null, comments: '', attachments: [], recorded_at: new Date() },
        }))
      );
      formik.setFieldValue('inspection_items', allItems);
    }
  }, [isEdit, formik]);

  useEffect(() => {
    const preload = async () => {
      const items = formik.values.inspection_items || [];
      const keys = [];
      items.forEach((item) => {
        (item.move_in_condition?.attachments || []).forEach((a) => {
          const k = getMediaKey(a);
          if (k) keys.push(k);
        });
        (item.move_out_condition?.attachments || []).forEach((a) => {
          const k = getMediaKey(a);
          if (k) keys.push(k);
        });
      });
      if (keys.length === 0) return;
      try {
        const urlMap = await getMultipleSignedUrls([...new Set(keys)]);
        setSignedUrlCache((prev) => ({ ...prev, ...urlMap }));
      } catch (err) {
        console.error('Preload signed URLs failed', err);
      }
    };
    if (formik.values.inspection_items?.length > 0) {
      preload();
    }
  }, [formik.values.inspection_items, getMediaKey]);

  const closeModal = () => {
    setIsModalOpen(false);
    setPendingMedia({});
    setSelectedMedia({ url: '', title: '', type: '' });
    setSignedUrlCache({});
  };

  const onDrop = useCallback(
    (acceptedFiles, itemIndex, phase) => {
      if (acceptedFiles.length === 0) return;
      if ((phase === 'move_in' && !allowMoveInEdit) || (phase === 'move_out' && !allowMoveOutEdit)) {
        Swal.fire('Locked', `This ${phase.replace('_', '-')} phase is not editable.`, 'info');
        return;
      }
      const key = `${itemIndex}-${phase}`;
      setPendingMedia((prev) => ({
        ...prev,
        [key]: [
          ...(prev[key] || []),
          ...acceptedFiles.map((f) => ({
            file: f,
            preview: URL.createObjectURL(f),
            name: f.name,
            type: f.type,
          })),
        ],
      }));
    },
    [allowMoveInEdit, allowMoveOutEdit]
  );

  const removePendingMedia = (itemIndex, phase, fileIndex) => {
    const key = `${itemIndex}-${phase}`;
    setPendingMedia((prev) => {
      const list = [...(prev[key] || [])];
      URL.revokeObjectURL(list[fileIndex].preview);
      list.splice(fileIndex, 1);
      return { ...prev, [key]: list };
    });
  };

  const uploadAllPendingMedia = async (inspectionId) => {
    if (Object.keys(pendingMedia).length === 0) return;
    const promises = [];
    for (const [keyStr, files] of Object.entries(pendingMedia)) {
      const [idxStr, phase] = keyStr.split('-');
      const itemIndex = parseInt(idxStr, 10);
      for (const { file } of files) {
        try {
          const { presignedUrl, key: s3Key } = await requestAttachmentUpload(inspectionId, {
            itemIndex,
            phase,
            fileName: file.name,
            contentType: file.type,
          });
          await fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });
          await confirmAttachmentUpload(inspectionId, { itemIndex, phase, key: s3Key });
          const condKey = phase === 'move_in' ? 'move_in_condition' : 'move_out_condition';
          const path = `inspection_items.${itemIndex}.${condKey}.attachments`;
          const current = formik.values.inspection_items?.[itemIndex]?.[condKey]?.attachments || [];
          formik.setFieldValue(path, [...current, s3Key]);
          promises.push(Promise.resolve());
        } catch (err) {
          console.error('Upload failed:', err);
          promises.push(Promise.reject(err));
        }
      }
    }
    await Promise.allSettled(promises);
    Object.values(pendingMedia).flat().forEach((f) => URL.revokeObjectURL(f.preview));
    setPendingMedia({});
  };

  const groupItemsBySection = () => {
    const groups = {};
    (formik.values.inspection_items || []).forEach((item, idx) => {
      const sec = item.section || 'other';
      if (!groups[sec]) groups[sec] = [];
      groups[sec].push({ ...item, index: idx });
    });
    return groups;
  };

  const inspectionId = formik.values._id;
  const isJointEditable = (phase) => phase === 1 || phase === 3;
  const isLandlordOnlyEditable = (phase) => phase === 2 || phase === 4;

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        toggle={closeModal}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: '99vw', width: '99vw' }}
      >
        <div className="d-flex justify-content-between align-items-center px-4" style={{ backgroundColor: '#0D568F', minHeight: '64px' }}>
          <h5 className="mb-0 text-white fw-normal">
            {previewMode ? (
              <>
                <FeatherIcon icon="eye" size={22} className="me-2" />
                Preview Move-In/Out Inspection
              </>
            ) : isEdit ? (
              <>
                <FeatherIcon icon="edit" size={22} className="me-2" />
                Edit Move-In/Out Inspection
              </>
            ) : (
              <>
                <FeatherIcon icon="file-text" size={22} className="me-2" />
                Create Move-In/Out Inspection
              </>
            )}
          </h5>
          <img src="/images/rentdigicare-logo.svg" alt="logo" style={{ height: '34px' }} />
          <FeatherIcon icon="x" size={22} className="me-2 text-white cursor-pointer" onClick={closeModal} />
        </div>

        <ModalBody className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
          {/* Property & Parties */}
          <Card className="shadow-sm border-0 mb-4">
            <CardBody>
              <div className="text-primary mb-3 d-flex align-items-center fw-semibold">
                <FeatherIcon icon="map-pin" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                Property & Parties Information
              </div>
              <Row className="g-4">
                <Col md={5}>
                  <FormGroup>
                    <Label className="fw-semibold">Rental Premises Address <span className="text-danger">*</span></Label>
                    <Input
                      name="property_details.address"
                      value={formik.values.property_details?.address || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.property_details?.address && !!formik.errors.property_details?.address}
                      placeholder="e.g. 123 Main Street, Edmonton, AB T5K 2J1"
                      disabled={!allowMoveInEdit}
                    />
                    {formik.touched.property_details?.address && formik.errors.property_details?.address && (
                      <small className="text-danger">{formik.errors.property_details.address}</small>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label className="fw-semibold">Unit / Suite Number</Label>
                    <Input
                      name="property_details.unit_number"
                      value={formik.values.property_details?.unit_number || ''}
                      onChange={formik.handleChange}
                      placeholder="e.g. 201, Basement, #5"
                      disabled={!allowMoveInEdit}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label className="fw-semibold">Landlord / Property Manager Name <span className="text-danger">*</span></Label>
                    <Input
                      name="property_details.landlord.name"
                      value={formik.values.property_details?.landlord?.name || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.property_details?.landlord?.name && !!formik.errors.property_details?.landlord?.name}
                      placeholder="John Doe"
                      disabled={!allowMoveInEdit}
                    />
                    {formik.touched.property_details?.landlord?.name && formik.errors.property_details?.landlord?.name && (
                      <small className="text-danger">{formik.errors.property_details.landlord.name}</small>
                    )}
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <Label className="fw-semibold d-block mb-3">
                    Tenants <span className="text-danger">*</span>
                    <span className="text-muted fs-6 ms-2">(at least one required)</span>
                  </Label>
                  {formik.values.property_details?.tenants?.length > 0 ? (
                    formik.values.property_details.tenants.map((tenant, i) => (
                      <Row key={i} className="align-items-end mb-3 g-3">
                        <Col md={5}>
                          <FormGroup>
                            <Label size="sm" className="fw-medium">Tenant Name ({i + 1}) <span className="text-danger">*</span></Label>
                            <Input
                              value={tenant.name || ''}
                              onChange={e => {
                                const tenants = [...(formik.values.property_details.tenants || [])];
                                tenants[i].name = e.target.value;
                                formik.setFieldValue('property_details.tenants', tenants);
                              }}
                              placeholder="Full name"
                              disabled={!allowMoveInEdit}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={5}>
                          <FormGroup>
                            <Label size="sm" className="fw-medium">Email <span className="text-danger">*</span></Label>
                            <Input
                              type="email"
                              value={tenant.email || ''}
                              onChange={e => {
                                const tenants = [...(formik.values.property_details.tenants || [])];
                                tenants[i].email = e.target.value;
                                formik.setFieldValue('property_details.tenants', tenants);
                              }}
                              placeholder="tenant@example.com"
                              disabled={!allowMoveInEdit}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={2}>
                          <Button
                            color="danger"
                            size="md"
                            outline
                            className="w-100"
                            onClick={() => {
                              const tenants = formik.values.property_details.tenants.filter((_, idx) => idx !== i);
                              formik.setFieldValue('property_details.tenants', tenants);
                            }}
                            disabled={!allowMoveInEdit || formik.values.property_details.tenants.length <= 1}
                          >
                            <FeatherIcon icon="trash-2" size={16} />
                          </Button>
                        </Col>
                      </Row>
                    ))
                  ) : (
                    <div className="text-center py-4 bg-light rounded">
                      <span className="text-muted">No tenants added yet</span>
                    </div>
                  )}
                  <div className="mt-3">
                    <Button
                      color="primary"
                      size="sm"
                      outline
                      onClick={() => {
                        const tenants = [...(formik.values.property_details?.tenants || [])];
                        formik.setFieldValue('property_details.tenants', [...tenants, { name: '', email: '' }]);
                      }}
                      disabled={!allowMoveInEdit}
                    >
                      <FeatherIcon icon="plus" size={16} className="me-2" /> Add Tenant
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Global Condition Modal - placed once here */}
          <GlobalConditionModal
            isOpen={globalConditionModal.isOpen}
            toggle={closeGlobalConditionModal}
            sectionKey={globalConditionModal.sectionKey}
            sectionTitle={
              sectionDisplayNames[globalConditionModal.sectionKey || '']
              || (globalConditionModal.sectionKey || '').replace(/_/g, ' ').toUpperCase()
            }
            allowEdit={globalConditionModal.phase === 'move_in' ? allowMoveInEdit : allowMoveOutEdit}
            phase={globalConditionModal.phase}
            formik={formik}
          />

          {/* Inspection Sections with auto-collapse */}
          {Object.entries(groupItemsBySection()).map(([sectionKey, groupItems]) => {
            const title = sectionDisplayNames[sectionKey] || sectionKey.replace(/_/g, ' ').toUpperCase();
            const icon = sectionIcons[sectionKey] || 'home';
            const isComplete = completedSections.has(sectionKey);
            const isOpen = isSectionOpen(sectionKey);

            return (
              <Card key={sectionKey} className="mb-4 shadow-sm border-0 overflow-hidden">
                <div
                  className="d-flex align-items-center justify-content-between p-4 text-primary bg-light"
                  onClick={() => toggleSection(sectionKey)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex align-items-center fw-semibold" style={{ fontSize: '0.95rem' }}>
                    <FeatherIcon icon={icon} size={18} strokeWidth={2.5} className="me-2" />
                    {title}
                    {isComplete && !isOpen && (
                      <span class="badge bg-success rounded-pill ms-2">
                        Complete
                      </span>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-4">
                    {groupItems.length} items
                    {(() => {
                      let totalSaved = 0;
                      let totalPending = 0;
                      groupItems.forEach((item) => {
                        const idx = item.index;
                        const keyIn = `${idx}-move_in`;
                        const keyOut = `${idx}-move_out`;
                        totalSaved += (item.move_in_condition?.attachments?.length || 0);
                        totalSaved += (item.move_out_condition?.attachments?.length || 0);
                        const pendIn = pendingMedia[keyIn]?.length || 0;
                        const pendOut = pendingMedia[keyOut]?.length || 0;
                        totalPending += pendIn + pendOut;
                      });
                      const grandTotal = totalSaved + totalPending;
                      if (grandTotal === 0) return null;
                      return (
                        <div
                          className="d-flex align-items-center gap-2 ps-2 border-start border-secondary-subtle"
                          style={{ fontSize: '0.9rem' }}
                        >
                          <FeatherIcon
                            icon="image"
                            size={16}
                            className={totalPending > 0 ? "text-warning" : "text-muted"}
                          />
                          <span className={totalPending > 0 ? "fw-medium text-warning" : "text-muted"}>
                            {grandTotal} media
                          </span>
                          {totalPending > 0 && (
                            <>
                              <span className="text-muted">·</span>
                              <span className="fw-medium text-warning">
                                {totalPending} pending
                              </span>
                              <FeatherIcon
                                icon="upload-cloud"
                                size={14}
                                className="text-warning ms-1"
                              />
                            </>
                          )}
                        </div>
                      );
                    })()}
                    <FeatherIcon
                      icon={isOpen ? 'chevron-up' : 'chevron-down'}
                      size={24}
                      className="text-primary ms-auto"
                    />
                  </div>
                </div>

                <Collapse isOpen={isOpen}>
                  <CardBody className="p-0">
                    <div className="table-responsive">
                      <Table hover className="moveinout-table align-middle mb-0">
                        <thead className="bg-light border-bottom border-2">
                          <tr>
                            <th className="text-muted fw-semibold text-uppercase text-start th-item">Item</th>
                            <th className="text-muted fw-semibold text-uppercase text-center">
                              Move-In Condition
                              {allowMoveInEdit && (
                                <FeatherIcon
                                  icon="sliders"
                                  size={12}
                                  className="text-primary ms-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openGlobalConditionModal(sectionKey, 'move_in');
                                  }}
                                />
                              )}
                            </th>
                            <th className="text-muted fw-semibold text-uppercase">Move-In Notes</th>
                            <th className="text-muted fw-semibold text-uppercase text-center">Move-In Media</th>
                            <th className="text-muted fw-semibold text-uppercase text-center">
                              Move-Out Condition
                              {allowMoveOutEdit && (
                                <FeatherIcon
                                  icon="sliders"
                                  size={12}
                                  className="text-danger ms-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openGlobalConditionModal(sectionKey, 'move_out');
                                  }}
                                />
                              )}
                            </th>
                            <th className="text-muted fw-semibold text-uppercase">Move-Out Notes</th>
                            <th className="text-muted fw-semibold text-uppercase text-center">Move-Out Media</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupItems.map((item) => (
                            <Fragment key={item.index}>
                              <InspectionItemRow
                                index={item.index}
                                name={item.name}
                                move_in_condition={item.move_in_condition}
                                move_out_condition={item.move_out_condition}
                                formik={formik}
                                onDrop={onDrop}
                                removePendingMedia={removePendingMedia}
                                setSelectedMedia={setSelectedMedia}
                                pendingMedia={pendingMedia}
                                signedUrlCache={signedUrlCache}
                                getMediaDisplayUrl={getMediaDisplayUrl}
                                isEdit={isEdit}
                                inspectionId={inspectionId}
                                allowMoveInEdit={allowMoveInEdit}
                                allowMoveOutEdit={allowMoveOutEdit}
                              />
                              <tr>
                                <td colSpan={7} className="p-0 border-bottom" style={{ opacity: 0.25 }} />
                              </tr>
                            </Fragment>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Collapse>
              </Card>
            );
          })}

          {/* Keys */}
          <Card className="shadow-sm border-0 mb-4">
            <CardBody className="p-4">
              <div className="text-primary mb-4 d-flex align-items-center fw-semibold">
                <FeatherIcon icon="lock" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                Keys Issued / Returned
              </div>
              <Row className="g-3">
                {['suite', 'building', 'entry', 'garage', 'mailbox', 'fob', 'parking_pass', 'discount_card'].map(keyType => (
                  <Col key={keyType}>
                    <div className="border rounded p-3 h-100 bg-white shadow-sm">
                      <Label className="text-capitalize fw-bold mb-2 text-secondary d-block small">
                        {keyType.replace(/_/g, ' ')}
                      </Label>
                      <div className="d-flex flex-column gap-3">
                        <div>
                          <small className="text-muted d-block mb-1 fw-medium">Issued</small>
                          <Input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={formik.values.keys?.issued?.[keyType] ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                formik.setFieldValue(`keys.issued.${keyType}`, '');
                              } else {
                                formik.setFieldValue(`keys.issued.${keyType}`, Number(val));
                              }
                            }}
                            disabled={!allowMoveInEdit}
                          />
                        </div>
                        <div>
                          <small className="text-muted d-block mb-1 fw-medium">Returned</small>
                          <Input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={formik.values.keys?.returned?.[keyType] ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                formik.setFieldValue(`keys.returned.${keyType}`, '');
                              } else {
                                formik.setFieldValue(`keys.returned.${keyType}`, Number(val));
                              }
                            }}
                            disabled={!allowMoveOutEdit}
                          />
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>

          {/* Signatures & Dates */}
          <Card className="shadow-sm border-0 mb-4">
            <CardBody className="p-5">
              <div className="text-primary mb-4 d-flex align-items-center fw-semibold">
                <FeatherIcon icon="file-text" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                Inspection Dates & Signatures
              </div>

              {/* MOVE-IN SECTION */}
              <div className="mb-5 p-4 bg-white border border-dark rounded">
                <h6 className="fw-bold text-center mb-3" style={{ fontSize: '1.1rem' }}>IN CONDITION</h6>
                <p className="mb-2" style={{ fontSize: '1rem' }}>
                  Inspections should be conducted when the premises are vacant unless the landlord and tenant or their agents otherwise agree.
                </p>

                <h6 className="fw-bold mb-3">WHEN THE LANDLORD AND TENANT INSPECT THE PREMISES TOGETHER: (Complete #1 and either #2a, b or c)</h6>
                <span className="mb-1">
                  1) The inspection of the premises was done on{' '}
                  <EditableDate
                    fieldPath="move_in.1.inspection_date"
                    formik={formik}
                    disabled={!allowMoveInEdit || !isJointEditable(currentPhase)}
                  />{' '}
                  by{' '}
                  <EditableText
                    fieldPath="move_in.1.landlord_name"
                    formik={formik}
                    placeholder="Landlord or Landlord's Agent"
                    disabled={!allowMoveInEdit || !isJointEditable(currentPhase)}
                  />{' '}
                  and by{' '}
                  <>
                    {(() => {
                      const tenants = formik.values.property_details?.tenants || [];
                      const validNames = tenants.map(t => t.name?.trim()).filter(Boolean);
                      return validNames.length > 0
                        ? validNames.length === 1
                          ? validNames[0]
                          : `${validNames.slice(0, -1).join(', ')} and ${validNames[validNames.length - 1]}`
                        : '____________________';
                    })()}
                  </>{' '}
                  (Tenant{formik.values.property_details?.tenants?.length > 1 ? 's' : ''} or Tenant's Agent{formik.values.property_details?.tenants?.length > 1 ? 's' : ''}).
                </span>

                <div className="mb-1">
                  <div className="d-flex align-items-end gap-2">
                    <span className="text-dark font-semibold">Signature of Landlord or Landlord's Agent:</span>
                    <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                      <DigitalSignatureField
                        fieldPath="move_in.1.landlord_signature"
                        formik={formik}
                        placeholder="Signature"
                        disabled={!allowMoveInEdit || !isJointEditable(currentPhase)}
                      />
                    </div>
                    <span className="text-dark font-semibold ms-3">Date:</span>
                    <EditableDate
                      fieldPath="move_in.1.landlord_signature_date"
                      formik={formik}
                      disabled={!allowMoveInEdit || !isJointEditable(currentPhase)}
                    />
                  </div>
                </div>

                <div>
                  <p className="mb-3">
                    <div className="d-flex align-items-end gap-2">
                      <span className="text-dark font-semibold">
                        2 a){' '}
                        <EditableText
                          fieldPath="move_in.2.a.tenant_name"
                          formik={formik}
                          placeholder="Tenant or Tenant's Agent"
                          disabled={
                            !allowMoveInEdit ||
                            !isJointEditable(currentPhase) ||
                            isOption2aDisabled('move_in')
                          }
                        />{' '}
                        , agree that this report fairly represents the condition of the premises.{' '}
                      </span>
                      <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                        <DigitalSignatureField
                          fieldPath="move_in.2.a.tenant_signature"
                          formik={formik}
                          placeholder="Signature of Tenant or Tenant's Agent"
                          disabled={
                            !allowMoveInEdit ||
                            !isJointEditable(currentPhase) ||
                            isOption2aDisabled('move_in')
                          }
                        />
                      </div>
                    </div>
                    {isOption2aDisabled('move_in') && (
                      <small className="text-muted d-block mt-1 ms-4">
                        (disabled — another option is selected)
                      </small>
                    )}
                  </p>

                  <p className="mb-3">
                    OR b){' '}
                    <EditableText
                      fieldPath="move_in.2.b.tenant_name"
                      formik={formik}
                      placeholder="Tenant or Tenant's Agent"
                      disabled={
                        !allowMoveInEdit ||
                        !isJointEditable(currentPhase) ||
                        isOption2bDisabled('move_in')
                      }
                    />{' '}
                    , disagree that this report fairly represents the condition of the premises for the following reasons:{' '}
                    <EditableText
                      fieldPath="move_in.2.b.disagreement_reasons"
                      formik={formik}
                      placeholder="Enter disagreement reasons..."
                      inputWidth="300px"
                      disabled={
                        !allowMoveInEdit ||
                        !isJointEditable(currentPhase) ||
                        isOption2bDisabled('move_in')
                      }
                    />
                  </p>

                  <p className="mb-3">
                    <div className="d-flex align-items-end gap-2">
                      <span className="text-dark font-semibold">
                        OR c) The Tenant or Tenant's agent present at the inspection refused to sign the Tenant's Statement.{' '}
                      </span>
                      <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                        <DigitalSignatureField
                          fieldPath="move_in.2.c.landlord_refusal_signature"
                          formik={formik}
                          placeholder="Signature of Landlord or Landlord's Agent"
                          disabled={
                            !allowMoveInEdit ||
                            !isJointEditable(currentPhase) ||
                            isOption2cDisabled('move_in')
                          }
                        />
                      </div>
                    </div>
                  </p>
                </div>

                <h6 className="fw-bold mt-4 mb-3">WHEN THE LANDLORD INSPECTS WITHOUT THE TENANT:</h6>
                <p className="mb-1">
                  3) The inspection of the premises was done on{' '}
                  <EditableDate
                    fieldPath="move_in.3.inspection_date"
                    formik={formik}
                    disabled={!allowMoveInEdit || !isLandlordOnlyEditable(currentPhase)}
                  />{' '}
                  by{' '}
                  <EditableText
                    fieldPath="move_in.3.landlord_name"
                    formik={formik}
                    placeholder="Landlord or Landlord's Agent"
                    disabled={!allowMoveInEdit || !isLandlordOnlyEditable(currentPhase)}
                  />{' '}
                  without the Tenant or Tenant's Agent being present.
                </p>

                <div className="mb-4">
                  <div className="d-flex align-items-end gap-2 flex-wrap">
                    <span className="text-dark font-semibold">Signature of Landlord or Landlord's Agent:</span>
                    <div className="border-bottom border-dark flex-grow-1 pb-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                      <DigitalSignatureField
                        fieldPath="move_in.3.landlord_signature"
                        formik={formik}
                        placeholder="Signature"
                        disabled={!allowMoveInEdit || !isLandlordOnlyEditable(currentPhase)}
                      />
                    </div>
                    <span className="text-dark font-semibold ms-3">Date:</span>
                    <EditableDate
                      fieldPath="move_in.3.landlord_signature_date"
                      formik={formik}
                      disabled={!allowMoveInEdit || !isLandlordOnlyEditable(currentPhase)}
                    />
                    <span className="text-dark font-semibold ms-3">TENANT FORWARDING ADDRESS:</span>
                    <EditableText
                      fieldPath="move_in.3.forwarding_address"
                      formik={formik}
                      placeholder="Enter forwarding address"
                      disabled={!allowMoveInEdit || !isLandlordOnlyEditable(currentPhase)}
                    />
                  </div>
                </div>
              </div>

              {/* MOVE-OUT SECTION */}
              <div className="p-4 bg-white border border-dark rounded">
                <h6 className="fw-bold text-center mb-3" style={{ fontSize: '1.1rem' }}>OUT CONDITION</h6>
                <p className="mb-2" style={{ fontSize: '1rem' }}>
                  Inspections should be conducted when the premises are vacant unless the landlord and tenant or their agents otherwise agree.
                </p>

                <h6 className="fw-bold mb-3">
                  WHEN THE LANDLORD AND TENANT INSPECT THE PREMISES TOGETHER: (Complete #1 and either #2a, b or c)
                  {!isJointEditable(currentPhase) && (
                    <small className="text-muted ms-2">(not editable in this phase)</small>
                  )}
                </h6>

                <span className="mb-1">
                  1) The inspection of the premises was done on{' '}
                  <EditableDate
                    fieldPath="move_out.1.inspection_date"
                    formik={formik}
                    disabled={!allowMoveOutEdit || !isJointEditable(currentPhase)}
                  />{' '}
                  by{' '}
                  <EditableText
                    fieldPath="move_out.1.landlord_name"
                    formik={formik}
                    placeholder="Landlord or Landlord's Agent"
                    disabled={!allowMoveOutEdit || !isJointEditable(currentPhase)}
                  />{' '}
                  and by{' '}
                  <>
                    {(() => {
                      const tenants = formik.values.property_details?.tenants || [];
                      const validNames = tenants.map(t => t.name?.trim()).filter(Boolean);
                      return validNames.length > 0
                        ? validNames.length === 1
                          ? validNames[0]
                          : `${validNames.slice(0, -1).join(', ')} and ${validNames[validNames.length - 1]}`
                        : '____________________';
                    })()}
                  </>{' '}
                  (Tenant{formik.values.property_details?.tenants?.length > 1 ? 's' : ''} or Tenant's Agent{formik.values.property_details?.tenants?.length > 1 ? 's' : ''}).
                </span>

                <div className="mb-1">
                  <div className="d-flex align-items-end gap-2">
                    <span className="text-dark font-semibold">Signature of Landlord or Landlord's Agent:</span>
                    <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                      <DigitalSignatureField
                        fieldPath="move_out.1.landlord_signature"
                        formik={formik}
                        placeholder="Signature"
                        disabled={!allowMoveOutEdit || !isJointEditable(currentPhase)}
                      />
                    </div>
                    <span className="text-dark font-semibold ms-3">Date:</span>
                    <EditableDate
                      fieldPath="move_out.1.landlord_signature_date"
                      formik={formik}
                      disabled={!allowMoveOutEdit || !isJointEditable(currentPhase)}
                    />
                  </div>
                </div>

                <div>
                  <p className="mb-3">
                    <div className="d-flex align-items-end gap-2">
                      <span className="text-dark font-semibold">
                        2 a){' '}
                        <EditableText
                          fieldPath="move_out.2.a.tenant_name"
                          formik={formik}
                          placeholder="Tenant or Tenant's Agent"
                          disabled={
                            !allowMoveOutEdit ||
                            !isJointEditable(currentPhase) ||
                            isOption2aDisabled('move_out')
                          }
                        />{' '}
                        , agree that this report fairly represents the condition of the premises.{' '}
                      </span>
                      <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                        <DigitalSignatureField
                          fieldPath="move_out.2.a.tenant_signature"
                          formik={formik}
                          placeholder="Signature of Tenant or Tenant's Agent"
                          disabled={
                            !allowMoveOutEdit ||
                            !isJointEditable(currentPhase) ||
                            isOption2aDisabled('move_out')
                          }
                        />
                      </div>
                    </div>
                    {isOption2aDisabled('move_out') && (
                      <small className="text-muted d-block mt-1 ms-4">
                        (disabled — another option is selected)
                      </small>
                    )}
                  </p>

                  <p className="mb-3">
                    OR b){' '}
                    <EditableText
                      fieldPath="move_out.2.b.tenant_name"
                      formik={formik}
                      placeholder="Tenant or Tenant's Agent"
                      disabled={
                        !allowMoveOutEdit ||
                        !isJointEditable(currentPhase) ||
                        isOption2bDisabled('move_out')
                      }
                    />{' '}
                    , disagree that this report fairly represents the condition of the premises for the following reasons:{' '}
                    <EditableText
                      fieldPath="move_out.2.b.disagreement_reasons"
                      formik={formik}
                      placeholder="Enter disagreement reasons..."
                      inputWidth="300px"
                      disabled={
                        !allowMoveOutEdit ||
                        !isJointEditable(currentPhase) ||
                        isOption2bDisabled('move_out')
                      }
                    />
                  </p>

                  <p className="mb-3">
                    <div className="d-flex align-items-end gap-2">
                      <span className="text-dark font-semibold">
                        OR c) The Tenant or Tenant's agent present at the inspection refused to sign the Tenant's Statement.{' '}
                      </span>
                      <div className="border-bottom border-dark flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                        <DigitalSignatureField
                          fieldPath="move_out.2.c.landlord_refusal_signature"
                          formik={formik}
                          placeholder="Signature of Landlord or Landlord's Agent"
                          disabled={
                            !allowMoveOutEdit ||
                            !isJointEditable(currentPhase) ||
                            isOption2cDisabled('move_out')
                          }
                        />
                      </div>
                    </div>
                  </p>
                </div>

                <h6 className="fw-bold mt-4 mb-3">
                  WHEN THE LANDLORD INSPECTS WITHOUT THE TENANT:
                  {!isLandlordOnlyEditable(currentPhase) && (
                    <small className="text-muted ms-2">(not editable in this phase)</small>
                  )}
                </h6>

                <p className="mb-1">
                  3) The inspection of the premises was done on{' '}
                  <EditableDate
                    fieldPath="move_out.3.inspection_date"
                    formik={formik}
                    disabled={!allowMoveOutEdit || !isLandlordOnlyEditable(currentPhase)}
                  />{' '}
                  by{' '}
                  <EditableText
                    fieldPath="move_out.3.landlord_name"
                    formik={formik}
                    placeholder="Landlord or Landlord's Agent"
                    disabled={!allowMoveOutEdit || !isLandlordOnlyEditable(currentPhase)}
                  />{' '}
                  without the Tenant or Tenant's Agent being present.
                </p>

                <div className="mb-4">
                  <div className="d-flex align-items-end gap-2 flex-wrap">
                    <span className="text-dark font-semibold">Signature of Landlord or Landlord's Agent:</span>
                    <div className="border-bottom border-dark flex-grow-1 pb-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                      <DigitalSignatureField
                        fieldPath="move_out.3.landlord_signature"
                        formik={formik}
                        placeholder="Signature"
                        disabled={!allowMoveOutEdit || !isLandlordOnlyEditable(currentPhase)}
                      />
                    </div>
                    <span className="text-dark font-semibold ms-3">Date:</span>
                    <EditableDate
                      fieldPath="move_out.3.landlord_signature_date"
                      formik={formik}
                      disabled={!allowMoveOutEdit || !isLandlordOnlyEditable(currentPhase)}
                    />
                    <span className="text-dark font-semibold ms-3">TENANT FORWARDING ADDRESS:</span>
                    <EditableText
                      fieldPath="move_out.3.forwarding_address"
                      formik={formik}
                      placeholder="Enter forwarding address"
                      disabled={!allowMoveOutEdit || !isLandlordOnlyEditable(currentPhase)}
                    />
                  </div>
                </div>
              </div>

              {/* Conclusions */}
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <div className="text-primary mb-4 d-flex align-items-center fw-semibold">
                    <FeatherIcon icon="edit-3" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                    Conclusions & Additional Notes
                  </div>
                  <Row className="g-4">
                    <Col md={12}>
                      <FormGroup>
                        <Label className="fw-bold">
                          Move-In Conclusions / Recommendations
                          {allowMoveInEdit && <span className="text-danger ms-1">*</span>}
                        </Label>
                        <Input
                          type="textarea"
                          rows={4}
                          value={formik.values.move_in_conclusions_recommendations || ''}
                          onChange={(e) =>
                            formik.setFieldValue('move_in_conclusions_recommendations', e.target.value)
                          }
                          placeholder="Enter conclusions or recommendations from the move-in inspection..."
                          disabled={!allowMoveInEdit}
                          invalid={
                            formik.touched.move_in_conclusions_recommendations &&
                            !!formik.errors.move_in_conclusions_recommendations
                          }
                        />
                        {formik.touched.move_in_conclusions_recommendations &&
                          formik.errors.move_in_conclusions_recommendations && (
                            <small className="text-danger">
                              {formik.errors.move_in_conclusions_recommendations}
                            </small>
                          )}
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <FormGroup>
                        <Label className="fw-bold">
                          Move-Out Conclusions / Recommendations
                          {allowMoveOutEdit && <span className="text-danger ms-1">*</span>}
                        </Label>
                        <Input
                          type="textarea"
                          rows={4}
                          value={formik.values.move_out_conclusions_recommendations || ''}
                          onChange={(e) =>
                            formik.setFieldValue('move_out_conclusions_recommendations', e.target.value)
                          }
                          placeholder="Enter conclusions or recommendations from the move-out inspection..."
                          disabled={!allowMoveOutEdit}
                          invalid={
                            formik.touched.move_out_conclusions_recommendations &&
                            !!formik.errors.move_out_conclusions_recommendations
                          }
                        />
                        {formik.touched.move_out_conclusions_recommendations &&
                          formik.errors.move_out_conclusions_recommendations && (
                            <small className="text-danger">
                              {formik.errors.move_out_conclusions_recommendations}
                            </small>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </CardBody>
          </Card>
        </ModalBody>

        <ModalFooter className="bg-light border-top">
          <Button color="secondary" onClick={closeModal}>
            {previewMode ? 'Close Preview' : 'Cancel'}
          </Button>
          {!previewMode && (
            <Button
              color="primary"
              size="lg"
              onClick={async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                  formik.setTouched(setNestedTouched(errors));
                  const isValid = await validateInspectionStep(
                    moveInOutInspectionSchema,
                    formik.values,
                    userRole || 'manager',
                    { pendingMedia }
                  );
                  if (!isValid) return;
                }
                try {
                  dispatch(setLoading(true));
                  let savedInspection;
                  let inspectionId = formik?.values?._id;
                  if (isEdit && inspectionId) {
                    savedInspection = await updateMoveInOutInspection(inspectionId, formik.values);
                    inspectionId = savedInspection?._id || inspectionId;
                  } else {
                    savedInspection = await createMoveInOutInspection(formik.values);
                    inspectionId = savedInspection?._id || savedInspection?.data?._id;
                  }
                  if (!inspectionId) throw new Error("No inspection ID returned from server");
                  if (Object.keys(pendingMedia).length > 0) {
                    await uploadAllPendingMedia(inspectionId);
                  }
                  closeModal();
                  if (onSaveSuccess) onSaveSuccess();
                  dispatch(setLoading(false));
                } catch (err) {
                  Swal.fire({
                    icon: 'error',
                    title: 'Operation failed',
                    text: err.message || 'Please try again or contact support.',
                  });
                  dispatch(setLoading(false));
                }
              }}
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {isEdit ? 'Saving…' : 'Creating…'}
                </>
              ) : isEdit ? (
                'Update Inspection'
              ) : (
                'Create Inspection'
              )}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <MediaViewerModal
        isOpen={!!selectedMedia.url}
        toggle={() => setSelectedMedia({ url: '', title: '', type: '' })}
        mediaUrl={selectedMedia.url}
        mediaType={selectedMedia.type}
        title={selectedMedia.title}
      />
    </>
  );
};

export { GenerateMoveInOutInspectionModal };